<script setup lang="ts">
import {nextTick, onMounted, onUnmounted, ref} from 'vue'

const show = ref(false)
const modalContentRef = ref<HTMLElement | null>(null)
const previousActiveElement = ref<HTMLElement | null>(null)
const modalTitleId = ref<string | undefined>(undefined)

const generateId = () => `modal-title-${Math.random().toString(36).substring(2, 9)}`

const openModal = () => {
  previousActiveElement.value = document.activeElement as HTMLElement
  show.value = true
  nextTick(() => {
    // Auto-detect heading for accessible name
    const heading = modalContentRef.value?.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6')
    if (heading) {
      if (!heading.id) {
        heading.id = generateId()
      }
      modalTitleId.value = heading.id
    }

    const focusableElement = modalContentRef.value?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    focusableElement?.focus()
  })
}

const closeModal = () => {
  show.value = false
  previousActiveElement.value?.focus()
}

const handleBackdropClick = () => {
  closeModal()
}

const handleKeydown = (event: KeyboardEvent) => {
  if (!show.value) return

  if (event.key === 'Escape') {
    closeModal()
    return
  }

  // Focus trap
  if (event.key === 'Tab' && modalContentRef.value) {
    const focusableElements = modalContentRef.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement?.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement?.focus()
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <div
    v-if="show"
    class="modal"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="modalTitleId"
  >
    <div
      class="modal-backdrop"
      @click="handleBackdropClick"
      aria-hidden="true"
    />
    <div
      ref="modalContentRef"
      class="relative z-10 w-11/12 md:w-6/12 md:mb-16 md:pb-8 shadow-none text-xl"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.7);
}
</style>
